
import { defineComponent, computed, PropType } from '@vue/composition-api';
import {
  formatSecondsToTime,
  formatSecondsToTimeWithMinutes
} from '@/composables/utils';
import IllinoisIcons from '../icons.vue';
import EventItem from './EventItem.vue';
import { LivescorePeriod, LivescoreIncident } from '../../../../../types/sport';
import IncidentsCodes from '../../IncidentsCodes.json';

const MAX_SECONDS = 90 * 60; // 90 minuti

export default defineComponent({
  name: 'IllinoisTimeline',
  components: { IllinoisIcons, EventItem },
  props: {
    currentSeconds: {
      type: Number,
      required: true
    },
    periods: {
      type: Array as PropType<LivescorePeriod[]>,
      required: false
    },
    sportId: {
      type: Number,
      required: false
    },
    eventId: {
      type: Number,
      required: false
    },
    home: {
      type: String,
      required: false
    },
    away: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      hoveredEventId: null,
      leftRef: null,
      rightRef: null,
      incidentsCodes: IncidentsCodes
    };
  },

  setup(props, { emit }) {
    const toNumber = (value: unknown): number => {
      const parsed = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const progressPercent = computed(() => {
      const seconds = toNumber(props.currentSeconds);
      const clamped = Math.max(0, Math.min(MAX_SECONDS, seconds));
      return Number(((clamped / MAX_SECONDS) * 100).toFixed(2));
    });

    const getProgressPercent = (): number => progressPercent.value;

    const eventLeftPercent = (seconds: number | string): number => {
      const value = toNumber(seconds);
      const clamped = Math.max(0, Math.min(MAX_SECONDS, value));
      return Number(((clamped / MAX_SECONDS) * 100).toFixed(2));
    };

    return {
      progressPercent,
      getProgressPercent,
      eventLeftPercent,
      formatSecondsToTime,
      formatSecondsToTimeWithMinutes
    };
  },
  methods: {
    openTooltip(id: string) {
      this.hoveredEventId = id;
    },
    closeTooltip() {
      this.hoveredEventId = null;
    },
    percentWithinSide(
      seconds: number | string,
      side: 'left' | 'right'
    ): number {
      const totalPercent: number = this.eventLeftPercent(seconds);
      if (side === 'left') {
        const p = Math.max(0, Math.min(50, totalPercent));
        return Number(((p / 50) * 100).toFixed(2));
      }
      const p = Math.max(0, Math.min(50, totalPercent - 50));
      return Number(((p / 50) * 100).toFixed(2));
    },
    getTooltipTransform(
      incident: LivescoreIncident,
      side: 'left' | 'right'
    ): string {
      const leftWidth: number = this.$refs.leftRef?.clientWidth || 0;
      const centerWidth: number = this.$refs.centerRef?.clientWidth || 0;
      const rightWidth: number = this.$refs.rightRef?.clientWidth || 0;
      const totalWidth: number = leftWidth + centerWidth + rightWidth;

      const containerWidth: number = side === 'left' ? leftWidth : rightWidth;
      if (!containerWidth || !totalWidth) {
        return 'translateX(-50%)';
      }

      // Larghezza reale tooltip (fallback 160px)
      const tooltipRefKey = `${side}-tooltip-${incident.Seconds}`;
      const rawTooltipRef: any = (this.$refs as any)?.[tooltipRefKey];
      const tooltipEl: HTMLElement | undefined = Array.isArray(rawTooltipRef)
        ? rawTooltipRef.find((el: any) => el && el.offsetParent !== null)
        : rawTooltipRef;
      const measuredWidth =
        tooltipEl && tooltipEl.clientWidth ? tooltipEl.clientWidth : 0;
      const tooltipWidthPx = measuredWidth > 0 ? measuredWidth : 160;

      // Larghezza marker evento (fallback 8px) per compensare -translate-x-1/2
      const eventRefKey = `${side}-event-${incident.Seconds}`;
      const rawEventRef: any = (this.$refs as any)?.[eventRefKey];
      const eventEl: HTMLElement | undefined = Array.isArray(rawEventRef)
        ? rawEventRef[0]
        : rawEventRef;
      const eventWidth =
        eventEl && eventEl.clientWidth ? eventEl.clientWidth : 8;
      const eventHalfWidth = eventWidth / 2;

      // Posizione evento nella metà e nel contesto globale
      const eventPercentWithinSide: number = this.percentWithinSide(
        incident.Seconds,
        side
      );
      const eventXLocal: number =
        (eventPercentWithinSide / 100) * containerWidth; // centro evento (locale)
      const eventLeftEdgeLocal: number = eventXLocal - eventHalfWidth;

      const sideGlobalOrigin: number =
        side === 'left' ? 0 : leftWidth + centerWidth;
      const eventXGlobal: number = sideGlobalOrigin + eventXLocal;

      // Centro tooltip desiderato rispetto allo spazio totale
      const desiredLeftGlobal: number = eventXGlobal - tooltipWidthPx / 2;
      const minLeftGlobal = 0;
      const maxLeftGlobal = Math.max(0, totalWidth - tooltipWidthPx);

      // Clamp globale: centra se c'è spazio, altrimenti allinea al bordo destro/sinistro
      const clampedLeftGlobal = Math.min(
        maxLeftGlobal,
        Math.max(minLeftGlobal, desiredLeftGlobal)
      );

      // Converti clamp globale in offset locale per il translateX
      const clampedLeftLocal = clampedLeftGlobal - sideGlobalOrigin;
      const offsetPx = Math.round(eventLeftEdgeLocal - clampedLeftLocal);

      const fixLastEvent =
        eventPercentWithinSide >= 99 ? offsetPx - 6 : offsetPx;
      return `translateX(-${fixLastEvent}px)`;
    }
  },
  computed: {
    leftFillPercent(): number {
      const p = this.getProgressPercent();
      return p <= 50 ? Number((p * 2).toFixed(2)) : 100;
    },
    rightFillPercent(): number {
      const p = this.getProgressPercent();
      return p <= 50 ? 0 : Number(((p - 50) * 2).toFixed(2));
    },
    allIncidents() {
      const base = Array.isArray(this.periods) ? this.periods : [];
      const incidents = base.flatMap(event => event.Incidents || []);
      return incidents;
    },
    leftIncidents() {
      return this.allIncidents.filter(
        (i: LivescoreIncident) => this.eventLeftPercent(i.Seconds) <= 50
      );
    },
    rightIncidents() {
      return this.allIncidents.filter(
        (i: LivescoreIncident) => this.eventLeftPercent(i.Seconds) > 50
      );
    }
  }
});
